import React, { useState, useEffect, useRef } from 'react';
import { Badge, Row, Col, Card, Alert } from 'react-bootstrap';
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaBriefcase,
    FaFileAlt,
    FaCheck,
    FaTimes,
    FaDownload,
    FaEye,
    FaClock,
    FaFilePdf,
    FaSpinner,
    FaIdCard
} from 'react-icons/fa';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import ContractService from '../../services/ContractService';
import '../../assets/css/ContractsPage.css';

// La configuration globale du worker pdf.js est maintenant gérée dans src/index.js

// Icône PDF
const PDF_ICON_URL = 'https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg';

// Données des services
const servicesData = {
    "garde_enfant": {
        name: "Garde d'enfant",
        icon: "assets/images/icon/2.png",
        subServices: [
            "Temps plein",
            "Baby-sitting ponctuel",
            "Assistance maternelle"
        ]
    },
    "aide_menagere": {
        name: "Aide ménagère",
        icon: "assets/images/icon/1.png",
        subServices: [
            "Nettoyage maison et bureau",
            "Aide ménagère complète",
            "Nettoyage spécialisé"
        ]
    },
    "aide_personnes_agees": {
        name: "Aide aux personnes âgées",
        icon: "assets/images/icon/3.png",
        subServices: [
            "Accompagnement personnalisé",
            "Aide au quotidien"
        ]
    },
    "soutien_scolaire": {
        name: "Soutien scolaire",
        icon: "assets/images/icon/4.png",
        subServices: [
            "Cours d'anglais",
            "Cours de mathématiques",
            "Cours d'informatique"
        ]
    }
};

const ContractModalClient = ({ contract, isOpen, onClose }) => {
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [isLoadingPdf, setIsLoadingPdf] = useState(false);
    const [activePdfTitle, setActivePdfTitle] = useState('');
    const [pdfError, setPdfError] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(true);

    if (!isOpen || !contract) return null;

    // ── Lecteur PDF intégré avec react-pdf ──────────────────────────────────
    const PdfReader = ({ url }) => {
        const [blobUrl, setBlobUrl] = useState(null);
        const [error, setError] = useState(false);
        const [loading, setLoading] = useState(true);
        const [pages, setPages] = useState(0);
        const [width, setWidth] = useState(320);
        const containerRef = useRef(null);

        useEffect(() => {
            let cancelled = false;
            let objectUrl = null;
            setLoading(true);
            setError(false);
            setBlobUrl(null);
            setPages(0);

            const load = async () => {
                try {
                    // Si l'URL est déjà un blob, l'utiliser directement
                    if (url && url.startsWith('blob:')) {
                        if (!cancelled) setBlobUrl(url);
                        setLoading(false);
                        return;
                    }

                    const response = await fetch(url);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const blob = await response.blob();
                    objectUrl = URL.createObjectURL(blob);
                    if (!cancelled) setBlobUrl(objectUrl);
                } catch (e) {
                    console.error('Erreur de lecture du document PDF :', e);
                    if (!cancelled) setError(true);
                    setLoading(false);
                }
            };
            if (url) load();

            return () => {
                cancelled = true;
                if (objectUrl) URL.revokeObjectURL(objectUrl);
            };
        }, [url]);

        // Largeur de page = largeur du conteneur (responsive)
        useEffect(() => {
            const updateWidth = () => {
                if (containerRef.current) {
                    setWidth(containerRef.current.clientWidth - 16);
                }
            };
            updateWidth();
            window.addEventListener('resize', updateWidth);
            return () => window.removeEventListener('resize', updateWidth);
        }, [blobUrl]);

        if (error) {
            return (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
                    <img src={PDF_ICON_URL} alt="PDF" style={{ width: 56, height: 56, marginBottom: 12 }} />
                    <h6>Aperçu indisponible</h6>
                    <p style={{ color: '#6c757d', marginTop: 8 }}>
                        Ce document PDF n'a pas pu être chargé pour lecture.
                    </p>
                </div>
            );
        }

        return (
            <div ref={containerRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#f1f3f5', padding: '0 8px 16px' }}>
                {(loading || !blobUrl) && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem 0' }}>
                        <FaSpinner className="fa-spin" size={32} color="#28a745" />
                        <p style={{ marginTop: 16, color: '#6c757d' }}>Chargement du document…</p>
                    </div>
                )}
                {blobUrl && (
                    <Document
                        file={blobUrl}
                        onLoadSuccess={({ numPages: n }) => { setPages(n); setLoading(false); }}
                        onLoadError={(e) => { console.error('Erreur pdf.js :', e); setError(true); setLoading(false); }}
                        loading={null}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                            {Array.from({ length: pages }, (_, i) => (
                                <div key={i} style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.15)', background: '#fff' }}>
                                    <Page pageNumber={i + 1} width={width > 0 ? width : 320} />
                                </div>
                            ))}
                        </div>
                    </Document>
                )}
            </div>
        );
    };

    // ── Fonction pour visualiser un PDF depuis une URL ──────────────────────
    const handleViewPdfFromUrl = async (url, title) => {
        if (!url) {
            alert('Aucun PDF disponible');
            return;
        }

        setIsLoadingPdf(true);
        setPdfError(false);
        setPdfLoading(true);

        try {
            // Si l'URL est déjà un blob, l'utiliser directement
            if (url.startsWith('blob:')) {
                setPdfBlobUrl(url);
                setActivePdfTitle(title);
                setShowPdfModal(true);
                setIsLoadingPdf(false);
                setPdfLoading(false);
                return;
            }

            // Sinon, télécharger le PDF via fetch
            const response = await fetch(url, {
                mode: 'cors',
                headers: {
                    'Accept': 'application/pdf',
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            setPdfBlobUrl(blobUrl);
            setActivePdfTitle(title);
            setShowPdfModal(true);
        } catch (error) {
            console.error('Erreur lors du chargement du PDF:', error);
            setPdfError(true);
            // Proposer le téléchargement direct si l'affichage échoue
            if (url && !url.startsWith('blob:')) {
                const shouldDownload = window.confirm('Impossible d\'afficher le PDF. Voulez-vous le télécharger ?');
                if (shouldDownload) {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `contrat_${contract.contractRef}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            } else {
                alert('Impossible de charger le PDF. Veuillez réessayer.');
            }
        } finally {
            setIsLoadingPdf(false);
            setPdfLoading(false);
        }
    };

    // ── Fonction pour générer un nouveau PDF ─────────────────────────────────
    const handleGenerateAndViewPdf = async () => {
        setIsLoadingPdf(true);
        setPdfError(false);
        try {
            const response = await ContractService.generateContractPdf(contract.id);
            const blob = new Blob([response], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            setPdfBlobUrl(blobUrl);
            setActivePdfTitle(`Contrat généré - ${contract.contractRef}`);
            setShowPdfModal(true);
        } catch (error) {
            console.error("Erreur lors du chargement du PDF:", error);
            setPdfError(true);
            alert('Erreur lors de la génération du PDF');
        } finally {
            setIsLoadingPdf(false);
        }
    };

    // ── Fermer la modal PDF ──────────────────────────────────────────────────
    const closePdfModal = () => {
        setShowPdfModal(false);
        if (pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
            URL.revokeObjectURL(pdfBlobUrl);
        }
        setPdfBlobUrl(null);
        setActivePdfTitle('');
        setPdfError(false);
        setPdfLoading(true);
    };

    // ── Télécharger le PDF ───────────────────────────────────────────────────
    const handleDownloadPdf = () => {
        if (pdfBlobUrl) {
            const link = document.createElement('a');
            link.href = pdfBlobUrl;
            link.download = `contrat_${contract.contractRef}_${new Date().getTime()}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // ── Formatage des dates ──────────────────────────────────────────────────
    const formatDate = (timestamp) => {
        if (!timestamp) return 'Non spécifié';
        return new Date(timestamp).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDesiredStartDate = (dateData) => {
        if (!dateData) return 'Non spécifiée';

        try {
            let date;

            if (typeof dateData === 'number' && dateData > 1000000000000) {
                date = new Date(dateData);
            }
            else if (Array.isArray(dateData)) {
                const [year, month, day] = dateData;
                date = new Date(year, month - 1, day);
            }
            else if (typeof dateData === 'string') {
                if (dateData.includes('T')) {
                    date = new Date(dateData);
                } else if (dateData.includes('-')) {
                    const [year, month, day] = dateData.split('-');
                    date = new Date(year, month - 1, day);
                } else {
                    return 'Format non supporté';
                }
            }
            else if (dateData instanceof Date) {
                date = dateData;
            } else {
                return 'Format non supporté';
            }

            if (isNaN(date.getTime())) {
                return 'Date invalide';
            }

            return date.toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });

        } catch (error) {
            console.error('Erreur de formatage:', error);
            return 'Erreur';
        }
    };

    // ── Badge de statut ──────────────────────────────────────────────────────
    const getStatusBadge = (status) => {
        switch (status) {
            case 'ACTIVE':
                return <Badge bg="success" className="rounded-pill px-3 py-2">Actif</Badge>;
            case 'EXPIRED':
                return <Badge bg="danger" className="rounded-pill px-3 py-2">Expiré</Badge>;
            case 'COMPLETED':
                return <Badge bg="primary" className="rounded-pill px-3 py-2">Terminé</Badge>;
            case 'PENDING_SIGNATURE':
                return <Badge bg="warning" className="rounded-pill px-3 py-2">En attente de signature</Badge>;
            default:
                return <Badge bg="secondary" className="rounded-pill px-3 py-2">Inconnu</Badge>;
        }
    };

    // ── Helpers ──────────────────────────────────────────────────────────────
    const getFullName = (user) => {
        if (!user) return 'Information non disponible';
        return `${user.firstName} ${user.lastName}`.trim();
    };

    const getEmail = (user) => {
        return user?.email || 'Email non disponible';
    };

    const getPhone = (user) => {
        return user?.phoneNumber || 'Téléphone non disponible';
    };

    const getLocation = (user) => {
        if (!user) return 'Localisation non disponible';
        return `${user.city}, ${user.country}`;
    };

    const getAvatarUrl = (user) => {
        return user?.profilePictureUrl || '/assets/images/logo/logo.png';
    };

    const hasPdfUrl = (url) => {
        return url && url.trim() !== '';
    };

    const DisplayField = ({ label, value, icon, emptyText = "Non spécifié" }) => (
        <div className="display-field mb-3">
            <div className="field-label">
                {icon} {label}
            </div>
            <div className="field-value bg-white rounded p-2">
                {value || emptyText}
            </div>
        </div>
    );

    return (
        <>
            {/* Modal principal */}
            <div
                className="contract-modal-overlay show"
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1050, overflowY: 'auto'
                }}
                onClick={onClose}
            >
                <div
                    className="contract-modal-container"
                    style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                        maxHeight: '95vh', overflowY: 'auto', animation: 'slideUp 0.4s ease'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header fixe */}
                    <div className="modal-header-fixed" style={{
                        position: 'sticky', top: 0, left: 0, right: 0, zIndex: 100,
                        backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                        borderTopLeftRadius: '24px', borderTopRightRadius: '24px', padding: '16px 24px'
                    }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <div>
                                <h4 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                    <FaFileAlt className="text-primary" />
                                    Détails du contrat
                                </h4>
                                <div className="d-flex align-items-center gap-2 mt-1">
                                    <span className="text-muted small">Référence: {contract.contractRef}</span>

                                </div>
                            </div>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>
                    </div>

                    {/* Contenu principal */}
                    <div className="p-4">
                        {/* Informations des parties */}
                        <Row className="g-4 mb-4">
                            <Col md={6}>
                                <div className="bg-light rounded-3 p-3 h-100">
                                    <h5 className="fw-bold mb-3 text-primary">
                                        <FaUser className="me-2" />
                                        Informations du client
                                    </h5>
                                    <div className="text-center mb-3">
                                        <img
                                            src={getAvatarUrl(contract.client?.user)}
                                            alt={getFullName(contract.client?.user)}
                                            className="rounded-circle"
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                objectFit: 'cover',
                                                border: '3px solid #28a745'
                                            }}
                                            onError={(e) => {
                                                e.target.src = '/assets/images/logo/logo.png';
                                            }}
                                        />
                                    </div>
                                    <div className="text-center">
                                        <h6 className="text-success fw-bold mb-2">
                                            {getFullName(contract.client?.user)}
                                        </h6>
                                        <div className="small text-muted">
                                            <FaEnvelope className="me-1" /> {getEmail(contract.client?.user)}<br />
                                            <FaPhone className="me-1" /> {getPhone(contract.client?.user)}<br />
                                            <FaMapMarkerAlt className="me-1" /> {getLocation(contract.client?.user)}
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className="bg-light rounded-3 p-3 h-100">
                                    <h5 className="fw-bold mb-3 text-info">
                                        <FaBriefcase className="me-2" />
                                        Informations du professionnel
                                    </h5>
                                    <div className="text-center mb-3">
                                        <img
                                            src={getAvatarUrl(contract.professional?.user)}
                                            alt={getFullName(contract.professional?.user)}
                                            className="rounded-circle"
                                            style={{
                                                width: '80px',
                                                height: '80px',
                                                objectFit: 'cover',
                                                border: '3px solid #0dcaf0'
                                            }}
                                            onError={(e) => {
                                                e.target.src = '/assets/images/logo/logo.png';
                                            }}
                                        />
                                    </div>
                                    <div className="text-center">
                                        <h6 className="text-info fw-bold mb-2">
                                            {getFullName(contract.professional?.user)}
                                        </h6>
                                        <div className="small text-muted">
                                            <FaEnvelope className="me-1" /> {getEmail(contract.professional?.user)}<br />
                                            <FaPhone className="me-1" /> {getPhone(contract.professional?.user)}<br />
                                            <FaMapMarkerAlt className="me-1" /> {getLocation(contract.professional?.user)}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Détails du contrat */}
                        <div className="bg-light rounded-3 p-3 mb-4">
                            <h5 className="fw-bold mb-3 text-primary">
                                <FaCalendarAlt className="me-2" />
                                Détails du contrat
                            </h5>
                            <Row className="g-3">
                                <Col md={6}>
                                    <DisplayField
                                        label="Date de début"
                                        value={formatDesiredStartDate(contract.startDate)}
                                        icon={<FaCalendarAlt className="text-primary" />}
                                    />
                                    <DisplayField
                                        label="Durée"
                                        value={`${contract.contractDuration} mois`}
                                        icon={<FaCalendarAlt className="text-primary" />}
                                    />
                                    <DisplayField
                                        label="Date de fin"
                                        value={formatDesiredStartDate(contract.endDate)}
                                        icon={<FaCalendarAlt className="text-primary" />}
                                    />
                                </Col>
                                <Col md={6}>
                                    {/* Contrat Généré */}
                                    <div className="display-field mb-3">
                                        <div className="field-label">
                                            <FaFilePdf className="me-1 text-danger" /> Contrat Généré
                                        </div>
                                        <div className="field-value bg-white rounded p-2">
                                            {hasPdfUrl(contract.generatedContractPresigned) || hasPdfUrl(contract.generatedContract) ? (
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <FaCheck className="text-success" />
                                                    <button
                                                        className="btn btn-link text-primary p-0 text-decoration-none"
                                                        onClick={() => handleViewPdfFromUrl(
                                                            contract.generatedContractPresigned || contract.generatedContract,
                                                            `Contrat généré - ${contract.contractRef}`
                                                        )}
                                                        disabled={isLoadingPdf}
                                                    >
                                                        <FaEye className="me-1" /> Voir le PDF
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <FaTimes className="text-danger" />
                                                    <span className="text-muted">Non généré</span>
                                                    <button
                                                        className="btn btn-sm btn-primary ms-auto"
                                                        onClick={handleGenerateAndViewPdf}
                                                        disabled={isLoadingPdf}
                                                    >
                                                        {isLoadingPdf ? (
                                                            <>
                                                                <FaSpinner className="fa-spin me-1" /> Génération...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaFilePdf className="me-1" /> Générer
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Signé par le client */}
                                    <div className="display-field mb-3">
                                        <div className="field-label">
                                            <FaUser className="me-1" /> Signé par le client
                                        </div>
                                        <div className="field-value bg-white rounded p-2">
                                            {contract.clientSigned ? (
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <FaCheck className="text-success" />
                                                    {hasPdfUrl(contract.contractDocClientSignedPresigned) || hasPdfUrl(contract.contractDocClientSigned) ? (
                                                        <button
                                                            className="btn btn-link text-primary p-0 text-decoration-none"
                                                            onClick={() => handleViewPdfFromUrl(
                                                                contract.contractDocClientSignedPresigned || contract.contractDocClientSigned,
                                                                `Contrat signé par client - ${contract.contractRef}`
                                                            )}
                                                            disabled={isLoadingPdf}
                                                        >
                                                            <FaEye className="me-1" /> Voir le PDF signé
                                                        </button>
                                                    ) : (
                                                        <span className="text-muted">Signé le {formatDate(contract.clientSignDate)}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaClock className="text-warning" />
                                                    <span className="text-muted">En attente de signature</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Signé par Driven Home Services */}
                                    <div className="display-field mb-3">
                                        <div className="field-label">
                                            <FaIdCard className="me-1" /> Signé par Driven Home Services
                                        </div>
                                        <div className="field-value bg-white rounded p-2">
                                            {contract.dhsSignedClient ? (
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <FaCheck className="text-success" />
                                                    {hasPdfUrl(contract.contractClientActivePresigned) || hasPdfUrl(contract.contractClientActive) ? (
                                                        <button
                                                            className="btn btn-link text-primary p-0 text-decoration-none"
                                                            onClick={() => handleViewPdfFromUrl(
                                                                contract.contractClientActivePresigned || contract.contractClientActive,
                                                                `Contrat final actif - ${contract.contractRef}`
                                                            )}
                                                            disabled={isLoadingPdf}
                                                        >
                                                            <FaEye className="me-1" /> Voir le PDF final
                                                        </button>
                                                    ) : (
                                                        <span className="text-muted">Signé le {formatDate(contract.dhsSignDate)}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center gap-2">
                                                    <FaClock className="text-warning" />
                                                    <span className="text-muted">En attente de signature</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Informations du service */}
                        {contract.professional && (
                            <div className="bg-light rounded-3 p-3 mb-4">
                                <h5 className="fw-bold mb-3 text-success">
                                    <FaBriefcase className="me-2" />
                                    Informations du Service sélectionné
                                </h5>
                                <div className="bg-white rounded p-3">
                                    <h6 className="text-primary fw-bold mb-2">{contract.serviceName}</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        <Badge bg="primary" className="rounded-pill px-3 py-2">
                                            <FaCheck className="me-1" /> {contract.subServiceName}
                                        </Badge>
                                        <Badge bg="success" className="rounded-pill px-3 py-2">
                                            <FaCheck className="me-1" /> {new Intl.NumberFormat('fr-FR').format(contract.serviceFee)} FCFA
                                        </Badge>
                                        {contract.frequency && (
                                            <Badge bg="info" className="rounded-pill px-3 py-2">
                                                <FaClock className="me-1" /> {contract.frequency}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal PDF - Style identique à ContractModal */}
            {showPdfModal && (
                <div
                    className="pdf-preview-overlay"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        zIndex: 1300,
                        overflowY: 'auto',
                    }}
                    onClick={closePdfModal}
                >
                    <div
                        className="pdf-preview-sheet"
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            borderRadius: '24px 24px 0 0',
                            height: '95vh',
                            maxHeight: '95vh',
                            display: 'flex',
                            flexDirection: 'column',
                            animation: 'pdfPreviewSlideUp 0.35s ease',
                            width: '100%',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header sticky */}
                        <div style={{
                            flex: '0 0 auto',
                            position: 'sticky',
                            top: 0,
                            zIndex: 5,
                            backgroundColor: 'white',
                            borderBottom: '1px solid #e2e8f0',
                            borderTopLeftRadius: '24px',
                            borderTopRightRadius: '24px',
                            padding: '16px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                                <img src={PDF_ICON_URL} alt="PDF" style={{ width: 26, height: 26, flexShrink: 0 }} />
                                <h6 style={{ margin: 0, fontWeight: 700, color: '#1a202c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {activePdfTitle || `Contrat ${contract.contractRef}`}
                                </h6>
                            </div>
                            <button
                                onClick={closePdfModal}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: '#6c757d',
                                    padding: '0.5rem',
                                    lineHeight: 1,
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Corps scrollable avec le lecteur PDF */}
                        <div style={{
                            flex: '1 1 auto',
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            padding: '0 0 0 0',
                        }}>
                            {isLoadingPdf ? (
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '4rem 0'
                                }}>
                                    <FaSpinner className="fa-spin" size={48} color="#28a745" />
                                    <p style={{ marginTop: 16, color: '#6c757d' }}>Chargement du document…</p>
                                </div>
                            ) : pdfError ? (
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    padding: '2rem'
                                }}>
                                    <img src={PDF_ICON_URL} alt="PDF" style={{ width: 56, height: 56, marginBottom: 12 }} />
                                    <h6>Aperçu indisponible</h6>
                                    <p style={{ color: '#6c757d', marginTop: 8 }}>
                                        Ce document PDF n'a pas pu être chargé pour lecture.
                                    </p>
                                    {pdfBlobUrl && (
                                        <button
                                            className="btn btn-outline-primary mt-3"
                                            onClick={handleDownloadPdf}
                                            style={{
                                                padding: '8px 24px',
                                                borderRadius: '50px',
                                                border: '1px solid #0d6efd',
                                                backgroundColor: 'transparent',
                                                color: '#0d6efd',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <FaDownload className="me-2" /> Télécharger le PDF
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <PdfReader url={pdfBlobUrl} />
                            )}
                        </div>

                        {/* Footer sticky */}
                        <div style={{
                            flex: '0 0 auto',
                            position: 'sticky',
                            bottom: 0,
                            backgroundColor: 'white',
                            borderTop: '1px solid #e2e8f0',
                            padding: '14px 20px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            flexWrap: 'wrap',
                        }}>
                            <button
                                onClick={closePdfModal}
                                style={{
                                    padding: '8px 24px',
                                    borderRadius: '50px',
                                    border: '1px solid #6c757d',
                                    backgroundColor: 'transparent',
                                    color: '#6c757d',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    fontSize: '0.95rem',
                                    fontWeight: 500,
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#6c757d';
                                    e.currentTarget.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#6c757d';
                                }}
                            >
                                Fermer
                            </button>
                            {pdfBlobUrl && !isLoadingPdf && !pdfError && (
                                <button
                                    onClick={handleDownloadPdf}
                                    style={{
                                        padding: '8px 24px',
                                        borderRadius: '50px',
                                        border: 'none',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        fontSize: '0.95rem',
                                        fontWeight: 500,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#c82333';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = '#dc3545';
                                    }}
                                >
                                    <FaDownload /> Télécharger
                                </button>
                            )}
                        </div>
                    </div>

                    <style>{`
                        @keyframes pdfPreviewSlideUp {
                            from { transform: translateY(100%); }
                            to { transform: translateY(0); }
                        }
                        .pdf-preview-overlay {
                            animation: fadeIn 0.3s ease;
                        }
                        @keyframes fadeIn {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                        @keyframes fa-spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                        .fa-spin {
                            animation: fa-spin 1s linear infinite;
                        }
                    `}</style>
                </div>
            )}

            <style jsx>{`
                @keyframes slideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                .contract-modal-overlay {
                    animation: fadeIn 0.3s ease;
                }

                .modal-header-fixed, .modal-footer-fixed {
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                }

                ::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }

                .field-label {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #2c3e50;
                    margin-bottom: 0.5rem;
                }

                .field-value {
                    font-size: 0.95rem;
                    color: #1a202c;
                }

                @keyframes fa-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .fa-spin {
                    animation: fa-spin 1s linear infinite;
                }
            `}</style>
        </>
    );
};

export default ContractModalClient;